import { Component } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ApiSettingsService } from "../api-settings.service";
import type { ApiToken } from "../../settings/settings.interface";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";

@Component({
  selector: "app-create-token-dialog",
  templateUrl: "./create-token-dialog.component.html",
  styleUrls: ["./create-token-dialog.component.scss"],
  standalone: false,
})
export class CreateTokenDialogComponent {
  form = new FormGroup({
    name: new FormControl("", (c) => Validators.required(c)),
    expiresIn: new FormControl<number | null>(null),
  });

  createdToken: ApiToken | null = null;
  copied = false;
  isLoading = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  public tokenCreated$ = new Subject<ApiToken>();

  constructor(
    private dialogRef: MatDialogRef<CreateTokenDialogComponent>,
    private apiSettingsService: ApiSettingsService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  onCreate() {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }
    this.isLoading = true;
    const name = this.form.value.name ?? "";
    const expiresInRaw = this.form.value.expiresIn;
    const expiresIn =
      expiresInRaw != null && Number(expiresInRaw) > 0
        ? Math.floor(Number(expiresInRaw))
        : undefined;
    this.apiSettingsService.createToken(name, expiresIn).subscribe({
      next: (token: ApiToken) => {
        this.createdToken = token;
        this.toastr.success(
          this.translate.instant("settings.api.tokenCreatedSuccess") as string,
        );
        this.tokenCreated$.next(token);
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.isLoading = false;
        const e = error as {
          error?: { message?: string } | string;
          message?: string;
        };
        const errorMessage =
          (typeof e.error === "object" && e.error !== null
            ? e.error.message
            : typeof e.error === "string"
              ? e.error
              : "") ||
          e.message ||
          "";
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("API_NOT_AVAILABLE")
        ) {
          this.dialogRef.close();
          this.dialog.open(LimitPlanDialogComponent, {
            width: "500px",
            maxWidth: "90vw",
            data: { plan: "basic", type: "api" },
          });
        } else {
          this.toastr.error(
            this.translate.instant("settings.api.tokenCreationError") as string,
          );
        }
      },
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  copyTokenToClipboard() {
    const key = this.createdToken?.key;
    if (key && !this.copied) {
      void navigator.clipboard.writeText(key);
      this.toastr.success(
        this.translate.instant("settings.api.copySuccess") as string,
      );
      this.copied = true;
    }
  }
}
